import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Button,
    FieldErrorList,
    FormField,
    Input,
    Label,
    MessageModal,
    Select
} from 'common/components';
import { Action, ButtonLabel, ButtonVariant, InputType, MessageType } from 'common/enums';
import { FieldError } from 'common/models';
import { ACTION_ITEMS, FAILURE_HEADER, MAIN_ROUTES } from 'common/config';
import { checkNumeric, validateInputString, validateMaxLength } from 'common/utils/validator';
import { addValidationError, removeValidationError } from 'common/utils/error';

import {
    VIDEO_LINK_ACCOUNT_ID_EMPTY_ERROR,
    VIDEO_LINK_ID_EMPTY_ERROR,
    VIDEO_LINK_ID_INVALID_CHAR_ERROR,
    VIDEO_LINK_ID_MAX_LENGTH,
    VIDEO_LINK_ID_MAX_LENGTH_ERROR,
    VIDEO_LINK_MAX_ACCOUNT_ID,
    VIDEO_LINK_STATUS,
    VIDEO_LINK_STATUS_EMPTY_ERROR,
    VIDEO_LINK_TITLE_EMPTY_ERROR,
    VIDEO_LINK_TITLE_INVALID_CHAR_ERROR,
    VIDEO_LINK_TITLE_MAX_LENGTH,
    VIDEO_LINK_TITLE_MAX_LENGTH_ERROR,
    VIDEO_LINK_VIDEO_TYPE_EMPTY_ERROR,
    VIDEO_LINK_ID_ALREADY_EXIST,
    VIDEO_LINK_CONFIRMATION_HEADER,
    VIDEO_LINK_TYPE_ALREADY_EXIST_ACTIVE_STATE,
    VIDEO_LINK_TYPE_INACTIVE_STATE
} from 'modules/configuration/config';
import {
    VideoDetails,
    VideoLinkDTO,
    selectVideoTypeArray
} from 'modules/configuration/models/configuration.model';
import {
    ConfirmationAction,
    VideoLinkField,
    VideoLinkStatus
} from 'modules/configuration/enums/configuration.enum';

import './AddEditVideoLinks.scss';
import {
    getVideoTypeData,
    saveUpdateVideoLinkUtil
} from 'modules/configuration/utils/addVideoLinks.utils';
import { getVideoLinkResponse } from 'modules/configuration/redux/configuration.selector';
import { RootState } from 'common/redux/core/root.reducer';
import { appStateActions } from 'common/redux/app-state';

const VideoLinks: FC = () => {
    const [fieldErrors, setFieldErrors] = useState<Array<FieldError>>([]);
    const [videoTypes, setVideoTypes] = useState<selectVideoTypeArray>([]);
    const [warningMessage, setWarningMessage] = useState('');
    const [data, setData] = useState<VideoLinkDTO>({
        status: false,
        title: '',
        accountId: '',
        videoType: '',
        videoDetailId: '',
        videoId: '',
        videoTypeId: ''
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { data: VideoLinkDTO; action: Action };
    const videoLinkResponse = useSelector((state: RootState) => getVideoLinkResponse(state));

    const [isConfirmationVisible, setConfirmationVisible] = useState<boolean>(false);

    const VideoTypeAddUpdateValidation = (warningMessageData: string) => {
        let errors = fieldErrors;
        errors = addValidationError(errors, 'sameVideoTypeIsActive', warningMessageData);
        setConfirmationVisible(true);
        setWarningMessage(warningMessageData);
        return errors;
    };

    const handleChange = (field: string, value: string | boolean) => {
        let errors: { field: string; message: string }[] = removeValidationError(
            fieldErrors,
            field
        );
        const accountIdReg = new RegExp(/^\d+$/);
        const alphaNumericRegex = new RegExp(/^[a-zA-Z0-9\s]*$/);
        if (field === VideoLinkField.Title) {
            if (validateMaxLength(value.toString(), VIDEO_LINK_TITLE_MAX_LENGTH)) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.Title,
                    VIDEO_LINK_TITLE_MAX_LENGTH_ERROR
                );
            }
            if (value.toString().trim().length === 0) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.Title,
                    VIDEO_LINK_TITLE_EMPTY_ERROR
                );
            } else if (!alphaNumericRegex.test(value.toString())) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.Title,
                    VIDEO_LINK_TITLE_INVALID_CHAR_ERROR
                );
            }
        } else if (field === VideoLinkField.VideoId) {
            if (validateMaxLength(value.toString(), VIDEO_LINK_ID_MAX_LENGTH)) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.VideoId,
                    VIDEO_LINK_ID_MAX_LENGTH_ERROR
                );
            }
            if (value.toString().trim().length === 0) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.VideoId,
                    VIDEO_LINK_ID_EMPTY_ERROR
                );
            } else if (!checkNumeric(value.toString())) {
                /** In user story 4538524 UI-Configurations for ‘Brightcove video Link’
                 * Brightcove Video ID (Mandatory)- Text field, Water mark shows from where user can pick the Brightcove Video ID.
                 *  Field should accept numeric characters. This field should accept maximum 50 characters.
                 *  Display error if not provided the value to the field- Brightcove Video ID is required.
                 * for this clarification asked for should accept numeric characters as per user story 4538524
                 */
                errors = addValidationError(
                    errors,
                    VideoLinkField.VideoId,
                    VIDEO_LINK_ID_INVALID_CHAR_ERROR
                );
            }
        } else if (field === VideoLinkField.AccountId) {
            if (validateMaxLength(value.toString(), VIDEO_LINK_MAX_ACCOUNT_ID)) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.AccountId,
                    VIDEO_LINK_ID_MAX_LENGTH_ERROR
                );
            }
            if (value.toString().trim().length === 0) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.AccountId,
                    VIDEO_LINK_ACCOUNT_ID_EMPTY_ERROR
                );
            } else if (!accountIdReg.test(value.toString())) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.AccountId,
                    VIDEO_LINK_ID_INVALID_CHAR_ERROR
                );
            }
            if (!validateInputString(value.toString())) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.AccountId,
                    VIDEO_LINK_ID_INVALID_CHAR_ERROR
                );
            }
        } else if (field === VideoLinkField.VideoTypeId) {
            if (!value && value) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.VideoTypeId,
                    VIDEO_LINK_VIDEO_TYPE_EMPTY_ERROR
                );
            }
        } else if (field === VideoLinkField.Status) {
            if (!value && value) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.Status,
                    VIDEO_LINK_STATUS_EMPTY_ERROR
                );
            }
        }

        setData({ ...data, [field]: value });
        setFieldErrors(errors);
    };

    const handleSave = () => {
        let errors = fieldErrors;
        if (!data[VideoLinkField.Title]?.trim()) {
            errors = addValidationError(errors, VideoLinkField.Title, VIDEO_LINK_TITLE_EMPTY_ERROR);
        }
        if (!data[VideoLinkField.VideoId].toString()?.trim()) {
            errors = addValidationError(errors, VideoLinkField.VideoId, VIDEO_LINK_ID_EMPTY_ERROR);
        }
        if (!data[VideoLinkField.AccountId]) {
            errors = addValidationError(
                errors,
                VideoLinkField.AccountId,
                VIDEO_LINK_ACCOUNT_ID_EMPTY_ERROR
            );
        }
        if (data[VideoLinkField.Status] && !data[VideoLinkField.Status]) {
            errors = addValidationError(
                errors,
                VideoLinkField.Status,
                VIDEO_LINK_STATUS_EMPTY_ERROR
            );
        }
        if (!data[VideoLinkField.VideoTypeId]) {
            errors = addValidationError(
                errors,
                VideoLinkField.VideoTypeId,
                VIDEO_LINK_VIDEO_TYPE_EMPTY_ERROR
            );
        }

        /**
         * Validation for 'Video ID already exist. Do you want to continue?'
         * 1. If video type and video id is same as existing video type and video id then show pop up with error
         * 2. If Video ID already exist do not allow to save the record and show pop up with error
         *
         */

        let sameVideoPresent = null;
        let sameVideoTypeIsActive = null;
        if (data[VideoLinkField.VideoTypeId] && data[VideoLinkField.VideoId]) {
            /**first condition video id and videoType id can not be same */
            sameVideoPresent = videoLinkResponse.data?.find((ele: VideoDetails) => {
                return (
                    ele.videoId === data[VideoLinkField.VideoId] &&
                    ele.videoTypeId === data[VideoLinkField.VideoTypeId]
                );
            });
            /** second condition same VideoType is present already in videos list and both new and old status are true
             * show popup for confirmation to disable old video and enable new video
             */
            sameVideoTypeIsActive = videoLinkResponse.data?.find((ele: VideoDetails) => {
                return ele.videoTypeId === data[VideoLinkField.VideoTypeId] && ele.status === true;
            });
        }

        if (
            data[VideoLinkField.VideoTypeId] &&
            data[VideoLinkField.VideoId] &&
            locationState?.action === Action.Create
        ) {
            if (sameVideoPresent) {
                errors = addValidationError(
                    errors,
                    VideoLinkField.VideoId,
                    VIDEO_LINK_ID_ALREADY_EXIST
                );
                /**show pop up with error */
                dispatch({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: VIDEO_LINK_ID_ALREADY_EXIST,
                        type: MessageType.Error
                    }
                });
            }

            if (!sameVideoPresent && !sameVideoTypeIsActive && !data.status) {
                errors = VideoTypeAddUpdateValidation(VIDEO_LINK_TYPE_INACTIVE_STATE);
            }
            // Upload Existing video type with Active State
            else if (!sameVideoPresent && sameVideoTypeIsActive && data.status) {
                errors = VideoTypeAddUpdateValidation(VIDEO_LINK_TYPE_ALREADY_EXIST_ACTIVE_STATE);
            }
            // Upload Existing video type with InActive State
            else if (!sameVideoPresent && sameVideoTypeIsActive && !data.status) {
                errors = VideoTypeAddUpdateValidation(VIDEO_LINK_TYPE_INACTIVE_STATE);
            }
        } else if (locationState?.action === Action.Edit) {
            // If user change the status
            if (locationState?.data.status !== data.status) {
                // User change the status Inactive Status
                if (!data.status) {
                    errors = VideoTypeAddUpdateValidation(VIDEO_LINK_TYPE_INACTIVE_STATE);
                }
                //If video type is active then same video type again user try to update active State
                else if (data.status && sameVideoTypeIsActive) {
                    errors = VideoTypeAddUpdateValidation(
                        VIDEO_LINK_TYPE_ALREADY_EXIST_ACTIVE_STATE
                    );
                }
            }
        }
        if (errors.length === 0) {
            saveUpdateVideoLinkUtil(data, locationState, dispatch, navigate);
        }
        setFieldErrors(errors);
    };

    const handleCancel = () => {
        navigate(MAIN_ROUTES.CONFIGURATION_VIDEO_LINKS);
    };
    const popupHandler = (action: ConfirmationAction) => {
        setConfirmationVisible(false);
        if (action === ConfirmationAction.OK) {
            saveUpdateVideoLinkUtil(data, locationState, dispatch, navigate);
        }
        setFieldErrors([]);
    };
    useEffect(() => {
        const updateData = async () => {
            await getVideoTypeData(dispatch, setVideoTypes);
            if (locationState?.action === Action.Edit) {
                setData({
                    accountId: locationState.data.accountId ?? '',
                    videoId: locationState.data.videoId ?? '',
                    status: locationState.data.status ?? '',
                    title: locationState.data.title ?? '',
                    videoType: locationState.data.videoType ?? '',
                    videoDetailId: locationState.data.videoDetailId ?? '',
                    videoTypeId: locationState.data.videoTypeId ?? ''
                });
            }
        };
        updateData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="route-container">
            <div className="video-links-header-container">
                <div className="video-links-header header-default">Video Link</div>
            </div>
            <div className="video-links-guide">Fill the details of Brightcove and video link</div>
            <div className="video-links-form">
                <FormField className="video-links-form-field odd">
                    <Label text="Title" />
                    <Input
                        type={InputType.TEXT}
                        value={data.title}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(VideoLinkField.Title, event.target.value)
                        }
                        onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(VideoLinkField.Title, event.target.value)
                        }
                    />
                    <FieldErrorList
                        field={VideoLinkField.Title}
                        errors={fieldErrors}
                        data-testid="error"
                    />
                </FormField>
                <FormField className="video-links-form-field">
                    <Label text="Brightcove Video ID" />
                    <Input
                        type={InputType.TEXT}
                        placeholder="Find Brightcove Video ID from Video URL or Right click on BrightCove Video and get 'Video ID' "
                        value={data.videoId}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(VideoLinkField.VideoId, event.target.value)
                        }
                        onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(VideoLinkField.VideoId, event.target.value)
                        }
                    />
                    <FieldErrorList
                        field={VideoLinkField.VideoId}
                        errors={fieldErrors}
                        data-testid="error"
                    />
                </FormField>
                <FormField className="video-links-form-field odd">
                    <Label text="Brightcove ID (Account ID)" />
                    <Input
                        type={InputType.TEXT}
                        placeholder="Find Brightcove Account ID from Video URL or Right click on BrightCove Video and get 'Account ID' "
                        value={data.accountId}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(VideoLinkField.AccountId, event.target.value)
                        }
                        onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(VideoLinkField.AccountId, event.target.value)
                        }
                    />
                    <FieldErrorList
                        field={VideoLinkField.AccountId}
                        errors={fieldErrors}
                        data-testid="error"
                    />
                </FormField>
                <FormField className="video-links-form-field">
                    <Label text="Video Type" />
                    <Select
                        value={data.videoTypeId}
                        options={videoTypes}
                        onChange={(selectedValue: string) =>
                            handleChange(VideoLinkField.VideoTypeId, selectedValue)
                        }
                        onBlur={(selectedValue: string) =>
                            handleChange(VideoLinkField.VideoTypeId, selectedValue)
                        }
                    />
                    <FieldErrorList
                        field={VideoLinkField.VideoTypeId}
                        errors={fieldErrors}
                        data-testid="error"
                    />
                </FormField>
                <FormField className="video-links-form-field odd">
                    <Label text="Status" />
                    <Select
                        value={data.status ? VideoLinkStatus.Active : VideoLinkStatus.Inactive}
                        options={VIDEO_LINK_STATUS}
                        onChange={(selectedValue: string) =>
                            handleChange(
                                VideoLinkField.Status,
                                selectedValue === VideoLinkStatus.Active
                            )
                        }
                        onBlur={(selectedValue: string) =>
                            handleChange(
                                VideoLinkField.Status,
                                selectedValue === VideoLinkStatus.Active
                            )
                        }
                    />
                    <FieldErrorList
                        field={VideoLinkField.Status}
                        errors={fieldErrors}
                        data-testid="error"
                    />
                </FormField>
            </div>
            <div className="video-links-footer">
                <Button
                    className="footer-button"
                    variant={ButtonVariant.Secondary}
                    onClick={handleCancel}>
                    {ACTION_ITEMS.CANCEL_BUTTON}
                </Button>
                <Button
                    className="footer-button"
                    variant={ButtonVariant.Primary}
                    disabled={fieldErrors.length != 0}
                    onClick={handleSave}>
                    {ACTION_ITEMS.SAVE_BUTTON}
                </Button>
            </div>
            <MessageModal
                headerName={VIDEO_LINK_CONFIRMATION_HEADER}
                message={warningMessage}
                isVisible={isConfirmationVisible}
                isSecondaryButtonVisible
                type={MessageType.Warning}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                onPrimaryAction={() => popupHandler(ConfirmationAction.OK)}
                onSecondaryAction={() => popupHandler(ConfirmationAction.CANCEL)}
            />
        </div>
    );
};

export default VideoLinks;
